import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_ALLOWED_PAGE_SIZES = [5, 10, 30, 50, 100];

export interface PaginationMeta {
  page?: number;
  limit?: number;
  totalPages?: number;
  totalCount?: number;
  allowedLimits?: number[];
}

export interface FetchItemsOptions {
  pageIndex?: number;
  pageSize?: number;
}

interface ParsedPage<T> {
  rows: T[];
  totalPages: number;
  totalCount?: number;
  allowedLimits?: number[];
}

interface FetchPageArgs<TFilters> {
  page: number;
  limit: number;
  filters: TFilters;
}

interface UseServerPaginatedListOptions<T, TFilters> {
  filters: TFilters;
  fetchPage: (args: FetchPageArgs<TFilters>) => Promise<any>;
  setRows: (rows: T[]) => void;
  normalizeRow?: (row: any) => T;
  sortRows?: (rows: T[]) => T[];
  isClientFallbackActive?: (filters: TFilters) => boolean;
  clientFilterRows?: (rows: T[], filters: TFilters) => T[];
  initialPageSize?: number;
  defaultAllowedPageSizes?: number[];
  debounceMs?: number;
  onError?: (error: unknown) => void;
}

const extractPage = <T>(
  payload: any,
  requestedPageSize: number,
): ParsedPage<T> => {
  const resultValue = payload?.result;
  const nestedResultData: T[] | undefined =
    resultValue &&
    typeof resultValue === "object" &&
    !Array.isArray(resultValue) &&
    Array.isArray(resultValue.data)
      ? resultValue.data
      : undefined;

  const rows: T[] = Array.isArray(resultValue)
    ? resultValue
    : nestedResultData ?? (Array.isArray(payload?.data) ? payload.data : []);

  const pagination: PaginationMeta =
    payload?.pagination ??
    resultValue?.pagination ??
    payload?.meta ??
    resultValue?.meta ??
    payload ??
    {};

  const rawTotalCount =
    pagination.totalCount ?? payload?.totalCount ?? resultValue?.totalCount;
  const parsedTotalCount = Number(rawTotalCount);

  const rawTotalPages =
    pagination.totalPages ??
    payload?.totalPages ??
    resultValue?.totalPages ??
    (Number.isFinite(parsedTotalCount)
      ? Math.ceil(parsedTotalCount / requestedPageSize)
      : 1);

  const parsedTotalPages = Number(rawTotalPages);
  const totalPages = Math.max(
    1,
    Number.isFinite(parsedTotalPages) ? parsedTotalPages : 1,
  );

  const rawAllowedLimits =
    pagination.allowedLimits ??
    payload?.allowedLimits ??
    resultValue?.allowedLimits;
  const allowedLimits = Array.isArray(rawAllowedLimits)
    ? rawAllowedLimits
        .map(Number)
        .filter((value: number) => Number.isFinite(value) && value > 0)
    : undefined;

  return {
    rows,
    totalPages,
    totalCount: Number.isFinite(parsedTotalCount)
      ? parsedTotalCount
      : undefined,
    allowedLimits,
  };
};

export function useServerPaginatedList<T, TFilters>({
  filters,
  fetchPage,
  setRows,
  normalizeRow,
  sortRows,
  isClientFallbackActive,
  clientFilterRows,
  initialPageSize = 10,
  defaultAllowedPageSizes = DEFAULT_ALLOWED_PAGE_SIZES,
  debounceMs = 350,
  onError,
}: UseServerPaginatedListOptions<T, TFilters>) {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);
  const [allowedPageSizes, setAllowedPageSizes] = useState<number[]>(
    defaultAllowedPageSizes,
  );
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  const requestIdRef = useRef(0);
  const fetchPageRef = useRef(fetchPage);
  const setRowsRef = useRef(setRows);
  const normalizeRowRef = useRef(normalizeRow);
  const sortRowsRef = useRef(sortRows);
  const fallbackRef = useRef(isClientFallbackActive);
  const clientFilterRef = useRef(clientFilterRows);
  const onErrorRef = useRef(onError);

  fetchPageRef.current = fetchPage;
  setRowsRef.current = setRows;
  normalizeRowRef.current = normalizeRow;
  sortRowsRef.current = sortRows;
  fallbackRef.current = isClientFallbackActive;
  clientFilterRef.current = clientFilterRows;
  onErrorRef.current = onError;

  const filtersKey = JSON.stringify(filters);
  const debouncedFiltersKey = JSON.stringify(debouncedFilters);
  const clientFallbackActive = Boolean(fallbackRef.current?.(debouncedFilters));
  const effectiveRequestPage = clientFallbackActive ? 0 : currentPage;

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setCurrentPage(0);
      setDebouncedFilters(filters);
    }, debounceMs);

    return () => window.clearTimeout(timer);
    // filtersKey is intentionally used to debounce a plain serializable filter object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, debounceMs]);

  const fetchItems = useCallback(
    async (options: FetchItemsOptions = {}): Promise<void> => {
      const requestedPageIndex = clientFallbackActive
        ? 0
        : (options.pageIndex ?? effectiveRequestPage);
      const requestedPageSize = options.pageSize ?? pageSize;
      const requestId = ++requestIdRef.current;

      try {
        setIsTableLoading(true);

        let rows: T[] = [];
        let nextTotalPages = 1;
        let nextTotalCount: number | undefined;
        let backendAllowedLimits: number[] | undefined;

        if (clientFallbackActive) {
          const fetchLimit = Math.max(...defaultAllowedPageSizes, 100);
          const firstResponse = await fetchPageRef.current({
            page: 1,
            limit: fetchLimit,
            filters: debouncedFilters,
          });
          const first = extractPage<T>(
            firstResponse?.data ?? firstResponse,
            fetchLimit,
          );
          rows = [...first.rows];
          backendAllowedLimits = first.allowedLimits;

          for (let page = 2; page <= first.totalPages; page += 1) {
            const response = await fetchPageRef.current({
              page,
              limit: fetchLimit,
              filters: debouncedFilters,
            });
            const parsed = extractPage<T>(
              response?.data ?? response,
              fetchLimit,
            );
            rows.push(...parsed.rows);
          }

          if (normalizeRowRef.current) {
            rows = rows.map((row) => normalizeRowRef.current!(row));
          }
          if (clientFilterRef.current) {
            rows = clientFilterRef.current(rows, debouncedFilters);
          }
          if (sortRowsRef.current) {
            rows = sortRowsRef.current(rows);
          }

          nextTotalCount = rows.length;
          nextTotalPages = Math.max(
            1,
            Math.ceil(rows.length / requestedPageSize),
          );
        } else {
          const response = await fetchPageRef.current({
            page: requestedPageIndex + 1,
            limit: requestedPageSize,
            filters: debouncedFilters,
          });
          const parsed = extractPage<T>(
            response?.data ?? response,
            requestedPageSize,
          );
          rows = parsed.rows;
          nextTotalPages = parsed.totalPages;
          nextTotalCount = parsed.totalCount;
          backendAllowedLimits = parsed.allowedLimits;

          if (normalizeRowRef.current) {
            rows = rows.map((row) => normalizeRowRef.current!(row));
          }
          if (sortRowsRef.current) {
            rows = sortRowsRef.current(rows);
          }
        }

        if (requestId !== requestIdRef.current) return;

        if (backendAllowedLimits?.length) {
          setAllowedPageSizes(backendAllowedLimits);
        }
        setTotalPages(nextTotalPages);
        setTotalCount(nextTotalCount);
        setRowsRef.current(rows);

        setCurrentPage((previousPage) =>
          previousPage >= nextTotalPages ? nextTotalPages - 1 : previousPage,
        );
      } catch (error) {
        if (requestId === requestIdRef.current) {
          onErrorRef.current?.(error);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setIsTableLoading(false);
        }
      }
    },
    [
      clientFallbackActive,
      debouncedFiltersKey,
      defaultAllowedPageSizes,
      effectiveRequestPage,
      pageSize,
    ],
  );

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  return {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination: !clientFallbackActive,
    fetchItems,
  };
}
